import { AuthOnFile } from "../data-model/medicalPaOrdersModel";
import { DocumentsStoreedInLocalStorage } from "../data-model/docsStorageInLocalStorage";
import { ValidDocumentTypesForUpload } from "../enums/documentTypes";
import { getAllJCodesFromAuthOnFile } from "../pages/singleOrderLayout/utils/existingDocsToLs";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../store/UserGeneralInfoStore";

const VALID_UPLOAD_DOC_TYPES = new Set<string>(
  Object.values(ValidDocumentTypesForUpload),
);

/**
 * Decide whether the Document Upload RPA should run for an order, based on
 * the J-codes attached to its `auth_on_file`.
 *
 * Returns true when at least one J-code in `authOnFile.entries` (or the
 * legacy `auth_entries` fallback handled by getAllJCodesFromAuthOnFile)
 * carries at least one document whose `doc_type` matches an entry in
 * {@link ValidDocumentTypesForUpload}. Otherwise returns false.
 *
 * Notes:
 * - `doc_path` is intentionally NOT checked — presence of a typed slot
 *   (uploaded or staged) is enough to signal that the RPA has work to do.
 *   Tighten to `doc_path !== ""` if "must already be uploaded" is required.
 * - Pure derivation from server state; does not read localStorage.
 */
export const determineIfDocumentRpaShouldRun = (
  authOnFile: AuthOnFile | null | undefined,
): boolean => {
  const jCodes = getAllJCodesFromAuthOnFile(authOnFile ?? undefined);
  if (!jCodes?.length) {
    return false;
  }
  return jCodes.some((jCode) =>
    (jCode?.documents ?? []).some(
      (doc) => !!doc?.doc_type && VALID_UPLOAD_DOC_TYPES.has(doc.doc_type),
    ),
  );
};

export const checkIfEveryDocTypeIsUploaded = (orderId: string) => {
  const allDocumentsForJCodesOfOrder = UserStore.getItem(
    `${UserGeneralInfoContextKeys.JCODE_DOCS}_${orderId}`,
  ) as Record<string, DocumentsStoreedInLocalStorage[]>;

  if (!allDocumentsForJCodesOfOrder) {
    return true;
  }
  return Object.values(allDocumentsForJCodesOfOrder).every(
    (doc) => doc.length === 0 || doc.every((d) => d.docPath !== ""),
  );
};
